import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router, UrlCreationOptions } from '@angular/router';
import { Store } from '@ngrx/store';

import {
  PAGE_CONTEXT_ID_URL_QUERY_PARAM,
  PageContextFacade,
  runtimeSelectors,
} from '@blaze-runtime/engine-data-access';
import { EnginePageNavigation, type PageNavigationRequest } from '@blaze-runtime/workflow-feature';
import { blazeActions, fromRouter, RuntimeMode } from '@blaze-shared/app-state-data-access';
import { RuntimePage } from '@blaze-shared/elements-domain';
import { NotificationService, syncSelect } from '@blaze-shared/platform-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import { ProjectsApiService } from '@blaze-shared/projects-data-access';
import { PageContext } from '@blaze-shared/projects-domain';
import { filterOutNullish } from '@blaze-shared/shared-util';
import { NamespaceId } from '@blaze-shared/workflow-domain';

import { DialogOverlayService } from './dialog/dialog-overlay.service';
import { UrlPageSlugService } from './url-page-slug.service';

interface PrepareNavigationDataInput {
  runtimeMode: RuntimeMode | undefined;
  projectName: string | undefined;
  targetPage: RuntimePage;
  pageDataRowId: string | undefined;
  navigationExtras: NavigationExtras;
  sanitizedUrl: string;
}

interface NavigateParams {
  bearerToken: string | undefined;
  projectId: string;
  isInEditor: boolean;
  projectName: string | undefined;
  projectView: string | undefined;
  publishedUrl: string;
  runtimeMode: RuntimeMode | undefined;
}

interface GetPageNavigationDataParams {
  targetPageId: string;
  openInNewTab: boolean | undefined;
  pageContext?: PageContext;
  targetPage?: RuntimePage | undefined;
  urlQueryParams?: Record<string, string>;
}
interface GetPageNavigationDataResult {
  navigationExtras: NavigationExtras;
  routeUrl: string;
  isInEditor: boolean;
  projectId: string;
  projectView: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class ProjectNavigateService extends EnginePageNavigation {
  private readonly dialogOverlayService = inject(DialogOverlayService);
  private readonly notificationService = inject(NotificationService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly projectsApiService = inject(ProjectsApiService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly urlPageSlugService = inject(UrlPageSlugService);

  navigateToPage({
    openInNewTab,
    pageContext,
    targetPageId,
    targetProjectId,
    urlQueryParams,
  }: PageNavigationRequest): void {
    if (!targetPageId) {
      throw new Error('Missing page id');
    }

    // Navigation to a different project is handled separately
    if (targetProjectId) {
      this.navigateToDifferentProject(targetProjectId, targetPageId, !!openInNewTab);
      return;
    }

    const targetPage = this.getTargetPage(targetPageId);
    if (!targetPage) {
      throw new Error('Page does not exist');
    }

    // Make sure the overlay gets closed before opening a new page
    if (!openInNewTab) {
      this.dialogOverlayService.closeCurrentDialog();
    }

    const { navigationExtras, routeUrl, isInEditor, projectId, projectView } =
      this.getPageNavigationData({
        pageContext,
        targetPageId,
        openInNewTab,
        targetPage,
        urlQueryParams,
      });

    // If page context present - set it in the store before navigation
    if (pageContext) {
      const currentPageId = syncSelect(this.store, runtimeSelectors.selectPageId);
      this.pageContextFacade.setPageContext(pageContext, currentPageId);
    }

    // Handle navigation in editor separately
    if (isInEditor) {
      this.navigateOrOpenNewTab(
        ['projects', projectId, projectView, targetPageId, pageContext?.pageDataRowId],
        navigationExtras,
        !!openInNewTab,
      );
      return;
    }

    // Navigate - open in new tab
    if (openInNewTab) {
      window.open(routeUrl, '_blank');
      return;
    }

    // Navigate - same tab
    this.store.dispatch(
      blazeActions.navigateToPage({
        projectId,
        pageId: targetPageId,
        runtimePage: targetPage,
      }),
    );
    this.router.navigateByUrl(routeUrl, navigationExtras);
  }

  openPreviewInNewTab(
    projectName: string,
    pageSlug: string,
    navigationExtras: UrlCreationOptions,
    pageDataRowId?: string,
  ): void {
    this.openNewTab(
      ['preview', urlUtils.normalizeProjectNameForUrl(projectName), pageSlug, pageDataRowId],
      navigationExtras,
    );
  }

  getPageNavigationData({
    targetPageId,
    openInNewTab,
    pageContext,
    targetPage,
    urlQueryParams,
  }: GetPageNavigationDataParams): GetPageNavigationDataResult {
    if (!targetPageId) {
      throw new Error('Missing page id');
    }

    targetPage = targetPage ?? this.getTargetPage(targetPageId);
    if (!targetPage) {
      throw new Error('Page does not exist');
    }

    const {
      bearerToken,
      projectName,
      isInEditor,
      projectId,
      projectView,
      publishedUrl,
      runtimeMode,
    } = this.getDataFromState();

    const navigationExtras: NavigationExtras = this.getNavigationExtras(
      runtimeMode,
      !!openInNewTab,
      pageContext,
      projectId,
      bearerToken,
      urlQueryParams,
    );

    // Prepare url to navigate to
    const routeUrl = this.prepareRouteUrl({
      runtimeMode,
      projectName,
      targetPage,
      pageDataRowId: pageContext?.pageDataRowId,
      navigationExtras,
      sanitizedUrl: this.sanitizeBearerFromUrl(publishedUrl, runtimeMode, bearerToken),
    });

    return { navigationExtras, routeUrl, isInEditor, projectId, projectView };
  }

  private getDataFromState(): NavigateParams {
    const bearerToken = syncSelect(this.store, runtimeSelectors.selectBearerToken);
    const projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);
    const isInEditor = syncSelect(this.store, fromRouter.getIsInEditor);
    const projectName = syncSelect(this.store, runtimeSelectors.selectProjectName);
    const projectView = syncSelect(this.store, fromRouter.getProjectView);
    const publishedUrl = syncSelect(this.store, runtimeSelectors.selectPublishedUrl);
    const runtimeMode = syncSelect(this.store, runtimeSelectors.selectRuntimeMode);

    return {
      bearerToken,
      isInEditor,
      projectId,
      projectName,
      projectView,
      publishedUrl,
      runtimeMode,
    };
  }

  private getTargetPage(targetPageId: NamespaceId): RuntimePage | undefined {
    const runtimePages = syncSelect(this.store, runtimeSelectors.selectRuntimePages);
    const targetPage = targetPageId ? runtimePages[targetPageId] : undefined;
    return targetPage;
  }

  private getNavigationExtras(
    runtimeMode: RuntimeMode | undefined,
    openInNewTab: boolean,
    pageContext: PageContext | undefined,
    projectId: string,
    bearerToken: string | undefined,
    urlQueryParams?: Record<string, string>,
  ): NavigationExtras {
    // This logic is for all modes
    const queryParams: NavigationExtras['queryParams'] = {
      ...urlQueryParams,
    };
    const state: NavigationExtras['state'] = {};

    // Add pageContextId to the History API's state
    // Used for back/forward navigation
    if (pageContext) {
      state['pageContextId'] = pageContext.id;
    }

    if (pageContext?.id) {
      queryParams[PAGE_CONTEXT_ID_URL_QUERY_PARAM] = pageContext.id;
    }

    // Preview mode requires projectId being passed as the query param all the time
    if (runtimeMode === RuntimeMode.Preview) {
      queryParams.projectId = projectId;
    }

    // Public Secure mode requires the bearer token to be passed as a query param all the time
    if (runtimeMode === RuntimeMode.PublicSecure && bearerToken) {
      queryParams.token = bearerToken;
    }

    return { queryParams, state };
  }

  private navigateOrOpenNewTab(
    urlParams: (string | undefined)[],
    navigationExtras: NavigationExtras,
    openInNewTab: boolean,
  ): void {
    if (openInNewTab) {
      this.openNewTab(urlParams, navigationExtras);
      return;
    }

    this.router.navigate(filterOutNullish(urlParams), navigationExtras);
  }

  private navigateToDifferentProject(
    projectId: string,
    targetPageId: string,
    openInNewTab: boolean,
  ): void {
    this.projectsApiService
      .getPublishedUrls(projectId)
      .pipe()
      .subscribe(({ signupUrl, url }) => {
        if (targetPageId === 'home' && !url) {
          this.notificationService.error(
            `Unable to open page. Please check whether target project is published.`,
          );
          return;
        } else if (targetPageId === 'signup' && !signupUrl) {
          this.notificationService.error(
            `Unable to open page. Please check whether sign up is enabled for the target project.`,
          );
          return;
        }

        const targetUrl = targetPageId === 'signup' ? `${url}/signup` : url;
        if (!targetUrl) {
          return;
        }
        if (openInNewTab) {
          window.open(targetUrl, '_blank');
        } else {
          console.log(`Redirecting to ${targetUrl}`);
          globalThis.location.href = targetUrl;
        }
      });
  }

  private openNewTab(
    urlParams: (string | undefined)[],
    navigationExtras: UrlCreationOptions,
  ): void {
    urlParams[0] = `/${urlParams[0]}`;
    const url = this.urlPageSlugService.createRouteUrl(
      filterOutNullish(urlParams),
      navigationExtras,
    );

    window.open(url, '_blank');
  }

  private prepareRouteUrl({
    runtimeMode,
    projectName,
    targetPage,
    pageDataRowId,
    navigationExtras,
    sanitizedUrl,
  }: PrepareNavigationDataInput): string {
    // Preview
    if (runtimeMode === RuntimeMode.Preview) {
      const routeUrl = this.urlPageSlugService.createRouteUrl(
        filterOutNullish([
          'preview',
          projectName ? urlUtils.normalizeProjectNameForUrl(projectName) : projectName,
          targetPage.slug,
          pageDataRowId,
        ]),
        navigationExtras,
      );
      return routeUrl;
    }

    // Published
    const routeUrl = this.urlPageSlugService.createPageSlugUrl(
      sanitizedUrl,
      targetPage,
      pageDataRowId,
      navigationExtras,
    );
    return routeUrl;
  }

  private sanitizeBearerFromUrl(
    publishedUrl: string,
    runtimeMode: RuntimeMode | undefined,
    bearerToken: string | undefined,
  ): string {
    if (runtimeMode === RuntimeMode.PublicSecure && bearerToken) {
      return publishedUrl.replaceAll(bearerToken, '');
    }

    return publishedUrl;
  }
}
